import * as React from 'react';
import { getIntrinsicElementProps, slot, useId } from '@fluentui/react-components';
import type { ScrollDownButtonProps, ScrollDownButtonState } from './ScrollDownButton.types';
import { ArrowDownFilled } from '@fluentui/react-icons';

// If you add JSX to this file, be sure to change the file type to .tsx

/**
 * Create the state required to render ScrollDownButton.
 *
 * The returned state can be modified with hooks such as useScrollDownButtonStyles_unstable,
 * before being passed to renderScrollDownButton_unstable.
 *
 * @param props - props from this instance of ScrollDownButton
 * @param ref - reference to root HTMLElement of ScrollDownButton
 */
export const useScrollDownButton_unstable = (
  props: ScrollDownButtonProps,
  ref: React.Ref<HTMLButtonElement>,
): ScrollDownButtonState => {
  const { isGenerating = false } = props;

  const maskId = useId('fai-spinner');

  return {
    isGenerating,
    maskId,
    components: {
      root: 'button',
      spinner: 'svg',
      spinnerMaskStroke: 'circle',
      spinnerMaskBackground: 'circle',
      spinnerBase: 'circle',
      icon: 'span',
    },
    root: slot.always(
      getIntrinsicElementProps('button', {
        ref,
        'aria-label': 'Scroll down',
        ...props,
      }),
      { elementType: 'button' },
    ),
    spinner: slot.optional(props.spinner, {
      renderByDefault: isGenerating,
      elementType: 'svg',
    }),
    spinnerMaskStroke: slot.optional(props.spinnerMaskStroke, {
      renderByDefault: isGenerating,
      elementType: 'circle',
      defaultProps: { cx: 0, cy: 0, r: 18 },
    }),
    spinnerMaskBackground: slot.optional(props.spinnerMaskBackground, {
      renderByDefault: isGenerating,
      elementType: 'circle',
      defaultProps: {
        cx: 0,
        cy: 0,
        r: 16,
        stroke: 'black',
        strokeWidth: 4,
        strokeLinecap: 'round',
        strokeDashoffset: 'calc(2 * pi * 16 * 0.25)',
        strokeDasharray: 'calc(2 * pi * 16 * 0.25), calc(2 * pi * 16 * 0.75)',
      },
    }),
    spinnerBase: slot.optional(props.spinnerBase, {
      renderByDefault: isGenerating,
      elementType: 'circle',
      defaultProps: { cx: 0, cy: 0, r: 18 },
    }),
    icon: slot.always(props.icon, {
      defaultProps: { children: <ArrowDownFilled /> },
      elementType: 'span',
    }),
  };
};
